import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Alert } from '../components/ui/Alert';
import { Card } from '../components/ui/Card';
import { MockBadge } from '../components/MockBadge';
import { BlockEditor } from '../components/BlockEditor';
import { HtmlTemplateEditor } from '../components/html-template/HtmlTemplateEditor';
import { StatusBadge } from '../components/ui/StatusBadge';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useFooterDefaults } from '../lib/useFooterDefaults';
import {
  blocksFromJson,
  blocksToHtml,
  seedBlocks,
  validateBlocks,
  type Block,
} from '../lib/blocks';
import type { Template, TemplateDetailResponse, TemplatePreviewResponse } from '../types/api';

/** A template without a usable saved document (old/foreign value) starts from a fresh default. */
function blocksFromTemplate(blockJson: unknown): Block[] {
  return blocksFromJson(blockJson) ?? seedBlocks();
}

/**
 * A template is either HTML or blocks, fixed for its life. The block editor
 * is only ever mounted for a block template: opening an HTML template in it
 * would replace its HTML with a fresh block layout on the next save.
 */
export function SkabelonEditPage() {
  const { id } = useParams<{ id: string }>();
  const { isEditor } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['template', id],
    queryFn: () => apiFetch<TemplateDetailResponse>(`/templates/${id}`),
    enabled: Boolean(id),
  });
  const template = data?.template;

  if (isLoading) return <div className="text-muted">Loading template…</div>;
  if (!template) return <Alert>The template was not found.</Alert>;

  return (
    <>
      <header className="flex items-center gap-3">
        <Link to="/skabeloner" className="no-underline">
          ← Templates
        </Link>
        <MockBadge />
      </header>
      <div className="flex items-center gap-3">
        <h1 className="m-0 text-2xl font-semibold tracking-tight">{template.name}</h1>
        {template.kind === 'html' ? (
          <StatusBadge tone="accent" icon="</>">
            HTML template
          </StatusBadge>
        ) : (
          <StatusBadge tone="neutral" icon="▦">
            Block template
          </StatusBadge>
        )}
      </div>
      {!isEditor ? <Alert>Only editors and administrators can edit templates.</Alert> : null}
      {template.kind === 'html' ? (
        <HtmlTemplateEditor template={template} readOnly={!isEditor} />
      ) : (
        <BlockTemplateEditor template={template} readOnly={!isEditor} />
      )}
    </>
  );
}

function BlockTemplateEditor({ template, readOnly }: { template: Template; readOnly: boolean }) {
  const id = template.id;
  const queryClient = useQueryClient();
  const data = { template };

  const [name, setName] = useState('');
  const [blocks, setBlocks] = useState<Block[]>(seedBlocks);
  const footerDefaults = useFooterDefaults();
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  useEffect(() => {
    if (data?.template) {
      setName(data.template.name);
      setBlocks(blocksFromTemplate(data.template.blockJson));
    }
  }, [data?.template]);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ template: Template }>(`/templates/${id}`, {
        method: 'PUT',
        body: { name, blockJson: { blocks }, html: blocksToHtml(blocks) },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['template', id] });
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  const previewMutation = useMutation({
    mutationFn: () =>
      apiFetch<TemplatePreviewResponse>(`/templates/${id}/preview`, {
        method: 'POST',
        body: { html: blocksToHtml(blocks) },
      }),
    onSuccess: (result) => setPreviewHtml(result.previewHtml),
  });

  const blockErrors = validateBlocks(blocks);

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="flex w-full max-w-[420px] flex-col gap-1.5">
          <Label htmlFor="t-name">Name</Label>
          <Input
            id="t-name"
            value={name}
            disabled={readOnly}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            disabled={previewMutation.isPending}
            onClick={() => previewMutation.mutate()}
          >
            {previewMutation.isPending ? 'Generating…' : 'Preview'}
          </Button>
          <Button
            variant="secondary"
            disabled={readOnly || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save template'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 max-[860px]:grid-cols-1 gap-4 items-start">
        <div>
          <h2 className="text-base font-semibold mb-2">Content</h2>
          {blockErrors.length > 0 ? (
            <Alert className="mb-3">
              <ul className="m-0 pl-4">
                {blockErrors.map((err) => (
                  <li key={err}>{err}</li>
                ))}
              </ul>
            </Alert>
          ) : null}
          <BlockEditor blocks={blocks} onChange={setBlocks} footerDefaults={footerDefaults} />
        </div>
        <div className="flex flex-col gap-3">
          {saveMutation.isError ? (
            <Alert>The template could not be saved. Check the content for errors.</Alert>
          ) : null}
          {saveMutation.isSuccess ? (
            <div className="text-success-text text-sm" role="status">
              Saved.
            </div>
          ) : null}
          {previewHtml !== null ? (
            <Card className="p-0 overflow-hidden">
              <iframe
                title="Preview"
                srcDoc={previewHtml}
                sandbox=""
                className="w-full h-[360px] border-0"
              />
            </Card>
          ) : null}
        </div>
      </div>
    </>
  );
}
